import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from '../../hook-utils';
import { InstantMessageConversation, InstantMessageConversationParticipant, InstantMessagerDeviceStatus, InstantMessagerParticipantEndpointScheme, InstantMessagerParticipantState } from '../../model';
import { actions } from '../../store';
import { selectExtension } from '../../store/reducers/queueAgents';
import ExtensionMenu from '../AgentMenu';
import { PresenceInfo } from '../AgentsTable';
import { ReactComponent as CloseIcon } from '../icons/cross.svg';
import './ConversationParticipant.scss';
import { MenuTarget, targetFromEvent } from '../Menu';
import { ImProfilePhoto } from './ImProfilePhoto';

export function ConversationParticipant({ conversation, participant }: { conversation: InstantMessageConversation, participant: InstantMessageConversationParticipant }) {
  const dispatch = useDispatch()
  const id = useMemo(() => ({ id: participant.endpointId, server: participant.endpointServer }), [participant.endpointId, participant.endpointServer])
  const ext = useSelector(s => selectExtension(s, id))
  const [menu, setMenu] = useState<MenuTarget>()

  useEffect(() => {
    if (!ext) {
      dispatch(actions.fetchExtensionsByIds([id]))
    }
  }, [dispatch, ext, id])

  const invite = getStatusLink(conversation, participant)
  return <div className={classNames('participant', !ext && 'presence-other')} onContextMenu={e => setMenu(targetFromEvent(e))}>
    {ext && <PresenceInfo className='presence' extension={ext} onClick={e => setMenu(e.currentTarget)} />}
    {ImProfilePhoto(participant.displayName, participant.endpointScheme, participant.endpointServer, participant.endpointId, 'photo')}
    <div>{participant.displayName}</div>
    <div>{conversation.conversationViewOnly === false && canRemove(conversation, participant) && <div title='Remove participant from conversation' className='icon'><CloseIcon className='close' onClick={() => dispatch(actions.requestRemoveParticipant(conversation.conversationId, participant.participantId))} /></div>}</div>
    <div className='status'>
      <div className={classNames(invite && 'link')} onClick={() => {
        if (invite && participant.participantId) {
          dispatch(actions.requestParticipantInvite(conversation.conversationId, participant.participantId))
        }
      }} title={getStatusTitle(conversation, participant)} ><img src={getStatusIcon(conversation, participant)} /> <div>{getStatusText(conversation, participant)}</div>
      </div>
    </div>
    {menu && ext && <ExtensionMenu target={menu} extension={ext} onClose={() => setMenu(undefined)} enabledIm={conversation.oneToOneConversation === false} />}
  </div >
}

function getStatusLink(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  let isStatusLink = false;
  if (isExtension(p)) {
    isStatusLink = (isOnline(p) && isGroupconversation(c) && !inConversation(p));
  } else {
    //This means its a contact or external contact which need to be email invitations
    //and then join the conversation via a webrowser interface
    if (isOnline(p) && inConversation(p) && p.typing) {
      isStatusLink = false;
    } else if (isOnline(p) && inConversation(p)) {
      isStatusLink = false;
    } else if (p.state === InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      isStatusLink = true;
    } else if (isAllInviteStage(c)) {
      isStatusLink = false;
    } else {
      isStatusLink = true;
    }
  }
  return isStatusLink;
}

function getStatusText(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  let onlineText = isOnline(p) ? 'Online' : 'Offline'
  let displayText = ''
  if (isExtension(p)) {
    if (!isOnline(p) && !isCurrentuser(c, p)) {
      displayText = 'Offline';
    } else if (!isCurrentuser(c, p) && isGroupconversation(c) && !inConversation(p) && p.state !== InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      displayText = 'Re-invite [Not in Conversation]';
    } else if (!isCurrentuser(c, p) && isGroupconversation(c) && !inConversation(p) && p.state === InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      displayText = 'Invite [Not in Conversation]';
    } else if (inConversation(p) && isGroupconversation(c) && !inConversation(p)) {
      displayText = 'Type a message to join';
    } else if (inConversation(p) && p.typing === true) {
      displayText = 'Is typing a message';
      //} else if (IsIMChatClientEnabled && this._isonline()) {
      // displayText = onlineText + ' [IM to Email]';
    } else {
      displayText = onlineText;
    }
  } else {
    //This means its a contact or external contact which need to be email invitations
    //and then join the conversation via a webrowser interface
    if (isOnline(p) && inConversation(p) && p.typing === true) {
      displayText = 'Is typing a message';
    } else if (isOnline(p) && inConversation(p)) {
      displayText = onlineText;
    } else if (p.state === InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      displayText = 'Re-invite [Invitation sent]';
    } else if (isAllInviteStage(c)) {
      displayText = 'Type a message to send an invite';
    } else {
      displayText = 'Send an invite';
    }
  }
  return displayText
}

function getStatusIcon(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  let icon = '../../Images/Icons/Chat/16_online.png';
  if (isExtension(p)) {
    if (!isOnline(p) && !isCurrentuser(c, p)) {
      icon = '../../Images/Icons/Chat/16_offline.png'
    } else if (isGroupconversation(c) && !inConversation(p) && p.state !== InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      icon = '../../Images/Icons/Chat/16_offline.png'
    } else if (inConversation(p) && p.typing === true) {
      icon = '../../Images/Icons/Chat/16_typing.png'
    } else {
      icon = '../../Images/Icons/Chat/16_online.png';
    }
  } else {
    //This means its a contact or external contact which need to be email invitations
    //and then join the conversation via a webrowser interface
    if (isOnline(p) && inConversation(p) && p.typing === true) {
      icon = '../../Images/Icons/Chat/16_typing.png';
    } else if (isOnline(p) && inConversation(p)) {
      icon = '../../Images/Icons/Chat/16_online.png';
    } else if (p.state === InstantMessagerParticipantState.ParticipantInvitedIntoConversation) {
      icon = '../../Images/Icons/Chat/16_email.png'
    } else if (isAllInviteStage(c)) {
      icon = '../../Images/Icons/Chat/16_email.png'
    } else {
      icon = '../../Images/Icons/Chat/16_offline.png'
    }
  }
  return icon
}

function getStatusTitle(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  let tooltip = isOnline(p) ? 'Online' : 'Offline';
  switch (p.state) {
    case InstantMessagerParticipantState.ParticipantEnteredConversation: tooltip += '\nEntered conversation'; break;
    case InstantMessagerParticipantState.ParticipantInConversation: tooltip += '\nIn conversation'; break;
    case InstantMessagerParticipantState.ParticipantInvitedIntoConversation: tooltip += '\nInvited into conversation'; break;
    case InstantMessagerParticipantState.ParticipantInviteStage: tooltip += '\nInvite stage'; break;
    case InstantMessagerParticipantState.ParticipantLeftConversation: tooltip += '\nLeft conversation'; break;
    case InstantMessagerParticipantState.ParticipantLostConnectionToConversation: tooltip += '\nLost connection to conversation'; break;
    case InstantMessagerParticipantState.ParticipantReEnteredConversation: tooltip += '\nRe-entered conversation'; break;
    case InstantMessagerParticipantState.ParticipantStateUnknownInternalUseOnly: tooltip += '\nUnknown conversation state'; break;
  }
  tooltip = tooltip + '\n' + ((isGroupconversation(c)) ? 'Group conversation' : 'One on one conversation') + '\n' + (inConversation(p) ? 'In conversation' : 'Not in conversation');
  return tooltip;
}

function isAllInviteStage(c: InstantMessageConversation) {
  for (let index = 0; index < c.participants.length; index++) {
    const p = c.participants[index];
    if (p.state !== InstantMessagerParticipantState.ParticipantInviteStage && p.participantId !== c.currentUserId) {
      return false
    }
  }
  return true
}

function canRemove(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  let isRemoveStatus = false;
  let isOnlineExtension = isExtension(p) && isOnline(p);
  if (isGroupconversation(c)) {
    if (p.state === InstantMessagerParticipantState.ParticipantLeftConversation)
      isRemoveStatus = true;
    if (p.state === InstantMessagerParticipantState.ParticipantInvitedIntoConversation && !isOnlineExtension)
      isRemoveStatus = true;
    if (p.state === InstantMessagerParticipantState.ParticipantInviteStage && !isOnlineExtension)
      isRemoveStatus = true;
    if (p.state === InstantMessagerParticipantState.ParticipantLostConnectionToConversation)
      isRemoveStatus = true;
    if (isExtension(p) && isGroupconversation(c) && !inConversation(p) && p.state !== InstantMessagerParticipantState.ParticipantInvitedIntoConversation)
      isRemoveStatus = true;
  }
  return isRemoveStatus && (p.participantId !== c.currentUserId)
}

function inConversation(p: InstantMessageConversationParticipant) {
  return p.state === InstantMessagerParticipantState.ParticipantEnteredConversation || p.state === InstantMessagerParticipantState.ParticipantInConversation || p.state === InstantMessagerParticipantState.ParticipantReEnteredConversation
}

function isGroupconversation(c: InstantMessageConversation) {
  //return c.oneToOneConversation !== undefined && !c.oneToOneConversation
  return c.participants.length > 2
}

function isOnline(participant: InstantMessageConversationParticipant) {
  return participant.status === InstantMessagerDeviceStatus.Online
}

function isExtension(participant: InstantMessageConversationParticipant) {
  return participant.endpointScheme === InstantMessagerParticipantEndpointScheme.IpfxExtension
}

function isCurrentuser(c: InstantMessageConversation, p: InstantMessageConversationParticipant) {
  return c.currentUserId === p.participantId
}
